import { ref, computed, watch, type Component } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useFilamentStore } from '@/stores/filament'
import { usePrinterStore } from '@/stores/printer'
import { useProjectStore } from '@/stores/project'
import { usePrintStore } from '@/stores/print'
import { useFileStore } from '@/stores/file'
import { useMaintenanceStore } from '@/stores/maintenance'
import { useLocationStore } from '@/stores/location'
import { Layers01Icon, PrinterIcon, WrenchIcon, Building01Icon, Folder01Icon, CubeIcon, CloudIcon } from '@/components/icons'

/* ─── types ─── */

export type SearchCategory =
  | 'filament'
  | 'printer'
  | 'project'
  | 'print'
  | 'file'
  | 'maintenance'
  | 'location'

export interface SearchResult {
  id: string
  label: string
  subtitle: string
  category: SearchCategory
  route: string
}

interface CategoryConfig {
  label: string
  icon: Component
  limit: number
}

export const CATEGORY_CONFIG: Record<SearchCategory, CategoryConfig> = {
  filament:    { label: 'Filaments',    icon: Layers01Icon,    limit: 5 },
  printer:     { label: 'Printers',     icon: PrinterIcon,     limit: 5 },
  project:     { label: 'Projects',     icon: Folder01Icon,    limit: 5 },
  print:       { label: 'Prints',       icon: CubeIcon,        limit: 5 },
  file:        { label: 'Files',        icon: CloudIcon,       limit: 5 },
  maintenance: { label: 'Maintenance',  icon: WrenchIcon,      limit: 5 },
  location:    { label: 'Locations',    icon: Building01Icon,  limit: 5 },
}

/* ─── helpers ─── */

/** Case-insensitive substring match against multiple fields */
function matchesQuery(query: string, ...fields: (string | undefined | null)[]): boolean {
  const q = query.toLowerCase()
  return fields.some((f) => f && f.toLowerCase().includes(q))
}

/* ─── composable ─── */

export function useGlobalSearch() {
  const router = useRouter()

  const query = ref('')
  const debouncedQuery = ref('')
  const isOpen = ref(false)
  const selectedIndex = ref(-1)

  // Debounce the query so we don't re-search on every keystroke
  const applyDebounce = useDebounceFn((val: string) => {
    debouncedQuery.value = val
  }, 200)

  watch(query, (val) => {
    applyDebounce(val)
    if (val.length < 2) {
      debouncedQuery.value = ''
    }
  })

  /* ── search across stores ── */

  const groupedResults = computed<Map<SearchCategory, SearchResult[]>>(() => {
    const map = new Map<SearchCategory, SearchResult[]>()
    const q = debouncedQuery.value.trim()
    if (q.length < 2) return map

    // Filaments
    const filamentStore = useFilamentStore()
    if (filamentStore._loaded) {
      const results: SearchResult[] = []
      for (const f of filamentStore.filaments) {
        if (results.length >= CATEGORY_CONFIG.filament.limit) break
        if (matchesQuery(q, f.name, f.brand, f.material, f.color, f.notes, f.storage_location, f.filament_code, f.upc)) {
          results.push({
            id: f.id,
            label: f.name || `${f.brand} ${f.material}`,
            subtitle: [f.brand, f.material].filter(Boolean).join(' · '),
            category: 'filament',
            route: `/filaments`,
          })
        }
      }
      if (results.length) map.set('filament', results)
    }

    // Printers
    const printerStore = usePrinterStore()
    if (printerStore._loaded) {
      const results: SearchResult[] = []
      for (const p of printerStore.printers) {
        if (results.length >= CATEGORY_CONFIG.printer.limit) break
        if (matchesQuery(q, p.name, p.manufacturer, p.model, p.notes)) {
          results.push({
            id: p.id,
            label: p.name,
            subtitle: [p.manufacturer, p.model].filter(Boolean).join(' · '),
            category: 'printer',
            route: `/printers/${p.id}`,
          })
        }
      }
      if (results.length) map.set('printer', results)
    }

    // Projects
    const projectStore = useProjectStore()
    if (projectStore._loaded) {
      const results: SearchResult[] = []
      for (const p of projectStore.projects) {
        if (results.length >= CATEGORY_CONFIG.project.limit) break
        const tagsStr = Array.isArray(p.tags) ? p.tags.join(' ') : ''
        if (matchesQuery(q, p.name, p.description, p.notes, tagsStr)) {
          results.push({
            id: p.id,
            label: p.name,
            subtitle: p.status || '',
            category: 'project',
            route: `/projects/${p.id}`,
          })
        }
      }
      if (results.length) map.set('project', results)
    }

    // Prints
    const printStore = usePrintStore()
    if (printStore._loaded) {
      const results: SearchResult[] = []
      for (const p of printStore.prints) {
        if (results.length >= CATEGORY_CONFIG.print.limit) break
        if (matchesQuery(q, p.name, p.file_name, p.notes)) {
          results.push({
            id: p.id,
            label: p.name,
            subtitle: p.status || '',
            category: 'print',
            route: `/prints/${p.id}`,
          })
        }
      }
      if (results.length) map.set('print', results)
    }

    // Files
    const fileStore = useFileStore()
    if (fileStore._loaded) {
      const results: SearchResult[] = []
      for (const f of fileStore.files) {
        if (results.length >= CATEGORY_CONFIG.file.limit) break
        if (matchesQuery(q, f.name, f.original_name, f.folder, f.file_type)) {
          results.push({
            id: f.id,
            label: f.name || f.original_name || 'Untitled',
            subtitle: [f.folder, f.file_type].filter(Boolean).join(' · '),
            category: 'file',
            route: '/files',
          })
        }
      }
      if (results.length) map.set('file', results)
    }

    // Maintenance
    const maintenanceStore = useMaintenanceStore()
    if (maintenanceStore._loaded) {
      const results: SearchResult[] = []
      for (const m of maintenanceStore.logs) {
        if (results.length >= CATEGORY_CONFIG.maintenance.limit) break
        if (matchesQuery(q, m.title, m.description, m.type, m.priority, m.notes)) {
          results.push({
            id: m.id,
            label: m.title || m.type,
            subtitle: [m.type, m.priority].filter(Boolean).join(' · '),
            category: 'maintenance',
            route: '/maintenance',
          })
        }
      }
      if (results.length) map.set('maintenance', results)
    }

    // Locations
    const locationStore = useLocationStore()
    if (locationStore._loaded) {
      const results: SearchResult[] = []
      for (const l of locationStore.locations) {
        if (results.length >= CATEGORY_CONFIG.location.limit) break
        if (matchesQuery(q, l.name, l.address, l.notes)) {
          results.push({
            id: l.id,
            label: l.name,
            subtitle: l.address || '',
            category: 'location',
            route: `/locations/${l.id}`,
          })
        }
      }
      if (results.length) map.set('location', results)
    }

    return map
  })

  const flatResults = computed<SearchResult[]>(() => {
    const flat: SearchResult[] = []
    for (const results of groupedResults.value.values()) {
      flat.push(...results)
    }
    return flat
  })

  const totalCount = computed(() => flatResults.value.length)
  const hasResults = computed(() => totalCount.value > 0)

  /* ── keyboard navigation ── */

  function moveDown() {
    if (flatResults.value.length === 0) return
    selectedIndex.value = (selectedIndex.value + 1) % flatResults.value.length
  }

  function moveUp() {
    if (flatResults.value.length === 0) return
    selectedIndex.value =
      selectedIndex.value <= 0
        ? flatResults.value.length - 1
        : selectedIndex.value - 1
  }

  function selectCurrent() {
    const result = flatResults.value[selectedIndex.value]
    if (result) navigateTo(result)
  }

  function navigateTo(result: SearchResult) {
    router.push(result.route)
    close()
  }

  /* ── open / close ── */

  function open() {
    isOpen.value = true
    selectedIndex.value = -1
  }

  function close() {
    isOpen.value = false
    query.value = ''
    debouncedQuery.value = ''
    selectedIndex.value = -1
  }

  // Reset selection when results change
  watch(flatResults, () => {
    selectedIndex.value = flatResults.value.length > 0 ? 0 : -1
  })

  return {
    query,
    isOpen,
    selectedIndex,
    groupedResults,
    flatResults,
    totalCount,
    hasResults,
    moveUp,
    moveDown,
    selectCurrent,
    navigateTo,
    open,
    close,
  }
}
